import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useQueryClient } from '@tanstack/react-query'
import OpalGrid from '../../../../organisms/DenaliAnalysis/tables/operational'
import SydButton from '../../../../commonDesign/Button'
import { useRetryMigrationTask } from '../../../../../api/documentVault'
import { QUERY_KEYS } from '../../../../../api/queryKeys'

const MigrationTasksTable = ({ tasks, total, disabled, loading, filter, onSortChange, onPageChange, sortState: externalSortState, pageState: externalPageState }) => {
  const queryClient = useQueryClient()
  const retryTask = useRetryMigrationTask()

  const handleRetry = useCallback(async (batchTaskId) => {
    await retryTask.mutateAsync(batchTaskId)
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.migrationBatches] })
  }, [retryTask, queryClient])

  const columns = useMemo(() => {
    const cols = [
      { accessorKey: 'batchTaskId', header: 'Task ID', size: 80 },
      {
        id: 'fileName',
        header: 'File Name',
        accessorFn: (row) => row.taskData?.fileName ?? '',
        cell: ({ getValue }) => (
          <div style={{ maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={getValue()}>
            {getValue()}
          </div>
        ),
        size: 280
      },
      {
        id: 'household',
        header: 'Household',
        accessorFn: (row) => row.taskData?.ocConsumerId ?? '',
        size: 100
      }
    ]

    if (filter === 'completed' || filter === 'all') {
      cols.push({
        id: 'documentKey',
        header: 'Document Key',
        accessorFn: (row) => row.resultData?.documentKey ?? '',
        cell: ({ getValue }) => getValue()
          ? <span style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'var(--color-neutral-500)' }}>{getValue()}</span>
          : '-',
        enableSorting: false,
        size: 250
      })
    }

    if (filter === 'errored' || filter === 'all') {
      cols.push({
        id: 'errorCode',
        header: 'Error',
        accessorFn: (row) => row.errorData?.errorCode ?? '',
        cell: ({ getValue }) => getValue()
          ? <span className='px-1 radius-sm font-size-xs font-bold' style={{ backgroundColor: 'var(--color-danger-050)', color: 'var(--color-danger-500)', fontFamily: 'monospace' }}>{getValue()}</span>
          : null,
        enableSorting: false,
        size: 160
      })
      cols.push({
        id: 'errorMessage',
        header: 'Message',
        accessorFn: (row) => row.errorData?.message ?? '',
        cell: ({ getValue }) => (
          <div style={{ maxWidth: '250px', fontSize: '0.8rem', color: 'var(--color-neutral-500)' }}>
            {getValue()}
          </div>
        ),
        enableSorting: false,
        size: 250
      })
    }

    if ((filter === 'errored' || filter === 'all') && !disabled) {
      cols.push({
        id: 'action',
        header: '',
        accessorFn: () => '',
        cell: ({ row }) => {
          if (!row.original.isError) return null
          return (
            <SydButton
              size='sm'
              variant='outline'
              icon='sync'
              onClick={() => handleRetry(row.original.batchTaskId)}
              disabled={disabled || retryTask.isLoading}
              processing={retryTask.isLoading}
            >
              Retry
            </SydButton>
          )
        },
        enableSorting: false,
        size: 100
      })
    }

    return cols
  }, [filter, disabled, handleRetry, retryTask.isLoading])

  return (
    <div>
      <OpalGrid
        data={tasks || []}
        rowCount={total || 0}
        columns={columns}
        isFetching={loading}
        pageState={externalPageState}
        sortState={externalSortState}
        onPageChange={onPageChange}
        onSortChange={onSortChange}
      />
    </div>
  )
}

MigrationTasksTable.propTypes = {
  tasks: PropTypes.array.isRequired,
  total: PropTypes.number.isRequired,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  filter: PropTypes.string,
  onSortChange: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  sortState: PropTypes.array.isRequired,
  pageState: PropTypes.object.isRequired
}

export default MigrationTasksTable
